// src/components/MeasurementsGizmo.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { formatCm } from "../utils/frameMeasurements.js";
import { useFrameMeasurements } from "../utils/useFrameMeasurements.js";

const INK = "#212121";
const PAPER = "#FBF9F3";

const TICK_LENGTH = 10;
const LABEL_OFFSET = 8;
// Rough glyph metrics for the 13px label, used to decide whether two labels
// would collide. Measuring for real (getComputedTextLength) would force a
// layout every frame; an estimate is plenty for a spacing test.
const CHAR_WIDTH = 6.6;
const LINE_HEIGHT = 13;
const LABEL_PADDING = 6;

/**
 * How far a label extends from its anchor along the direction (ux, uy).
 * A horizontal run has to clear the full text width, a vertical run only the
 * line height — which is why the vertical and horizontal axes need different
 * spacing without either being special-cased.
 */
function labelExtent(el, ux, uy) {
  const chars = (el?.textContent || "").length;
  return (
    Math.abs(ux) * ((chars * CHAR_WIDTH) / 2) + Math.abs(uy) * (LINE_HEIGHT / 2)
  );
}
// How far the total-dimension labels sit outside the frame's outer edge.
const TOTAL_LABEL_OFFSET = 26;
const TOTAL_LINE_HEIGHT = 16;

const gizmoStyles = {
  svg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    // Under LoadingScreen's 1000 so the loader still covers it.
    zIndex: 900,
    transition: "opacity 200ms ease",
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  label: {
    // Start hidden; drawAxis/placeTotals reveal each label once it has a
    // transform. Without this, a label paints at the SVG origin for the frames
    // before its first placement.
    display: "none",
    fill: INK,
    fontSize: "13px",
    fontWeight: 500,
    letterSpacing: "-0.01em",
    // Halo the text so it stays legible over the artwork without needing a
    // measured backing rect.
    stroke: PAPER,
    strokeWidth: 3,
    paintOrder: "stroke",
    strokeLinejoin: "round",
  },
};

/**
 * Draw one measurement axis: the polyline through its projected boundary
 * points, a tick at each boundary, and a number beside each segment.
 *
 * Works for any orientation — everything is derived from the direction of each
 * segment on screen, so the vertical and horizontal runs share this code.
 */
function drawAxis(points, axis, refs) {
  const { segments } = axis;
  const { line, ticks, labels } = refs;
  if (!line || !ticks) return;

  // An empty set is the probe explicitly standing down — clear the drawing.
  if (!points || points.length === 0) {
    line.setAttribute("points", "");
    ticks.setAttribute("d", "");
    return;
  }

  // A count mismatch just means the segment structure changed and the probe has
  // not re-projected yet. Leave the previous paint up for that one frame rather
  // than blinking the line off.
  //
  // Compare against the axis's own point count, NOT segments.length + 1 —
  // segments are filtered (a frameless print drops both profiles), so the two
  // only agree when every segment survives.
  if (points.length !== axis.points.length) return;

  const tickPath = [];
  const placements = [];
  let anyDrawn = false;

  segments.forEach((segment, index) => {
    const from = points[segment.startIndex];
    const to = points[segment.startIndex + 1];

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (!Number.isFinite(length) || length < 1e-6) {
      placements.push(null);
      return;
    }

    // Perpendicular, rotated a quarter turn from the run direction.
    const perpX = dy / length;
    const perpY = -dx / length;

    for (const point of [from, to]) {
      const hx = (perpX * TICK_LENGTH) / 2;
      const hy = (perpY * TICK_LENGTH) / 2;
      tickPath.push(
        `M${point.x - hx} ${point.y - hy}L${point.x + hx} ${point.y + hy}`
      );
    }

    anyDrawn = true;
    placements.push({
      index,
      x: (from.x + to.x) / 2 + perpX * LABEL_OFFSET,
      y: (from.y + to.y) / 2 + perpY * LABEL_OFFSET,
    });
  });

  ticks.setAttribute("d", tickPath.join(""));

  // Show every label that physically fits. A thin profile or mat gets its
  // number whenever the neighbouring label is far enough away, and only the
  // genuinely cramped ones are dropped.
  let lastPlaced = null;
  placements.forEach((placement) => {
    if (!placement) return;
    const label = labels[placement.index];
    if (!label) return;

    let clear = true;
    if (lastPlaced !== null) {
      const dx = placement.x - lastPlaced.x;
      const dy = placement.y - lastPlaced.y;
      const distance = Math.hypot(dx, dy);
      const ux = distance > 1e-6 ? dx / distance : 0;
      const uy = distance > 1e-6 ? dy / distance : 0;
      const needed =
        labelExtent(labels[lastPlaced.index], ux, uy) +
        labelExtent(label, ux, uy) +
        LABEL_PADDING;
      clear = distance >= needed;
    }

    if (clear) {
      label.style.display = "";
      label.setAttribute(
        "transform",
        `translate(${placement.x} ${placement.y})`
      );
      lastPlaced = placement;
    } else {
      label.style.display = "none";
    }
  });

  if (!anyDrawn) {
    line.setAttribute("points", "");
    return;
  }

  // A polyline through every projected point, not a straight line: the camera
  // is perspective, so the boundaries genuinely bend on screen when rotated.
  const first = segments[0].startIndex;
  const last = segments[segments.length - 1].startIndex + 1;
  const coords = [];
  for (let i = first; i <= last; i++) {
    coords.push(`${points[i].x},${points[i].y}`);
  }
  line.setAttribute("points", coords.join(" "));
}

/**
 * Park a total-dimension label just outside the midpoint of one outer edge.
 *
 * `corners` are the projected outer corners, clockwise from top-left. The push
 * direction is derived from the frame's own centre, so the labels stay outside
 * the frame no matter how the camera is rotated.
 */
function placeTotals(corners, widthEl, heightEl, widthPosition, heightPosition) {
  const hide = () => {
    if (widthEl) widthEl.style.display = "none";
    if (heightEl) heightEl.style.display = "none";
  };
  if (!corners || corners.length !== 4) return hide();

  const cx = (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4;
  const cy = (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return hide();

  const place = (el, a, b) => {
    if (!el) return;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = mx - cx;
    const dy = my - cy;
    const length = Math.hypot(dx, dy);
    if (!Number.isFinite(length) || length < 1e-6) {
      el.style.display = "none";
      return;
    }
    el.style.display = "";
    el.setAttribute(
      "transform",
      `translate(${mx + (dx / length) * TOTAL_LABEL_OFFSET} ${
        my + (dy / length) * TOTAL_LABEL_OFFSET
      })`
    );
  };

  const [topLeft, topRight, bottomRight, bottomLeft] = corners;
  place(
    widthEl,
    ...(widthPosition === "above"
      ? [topLeft, topRight]
      : [bottomLeft, bottomRight])
  );
  place(
    heightEl,
    ...(heightPosition === "right"
      ? [topRight, bottomRight]
      : [topLeft, bottomLeft])
  );
}

/**
 * 2D overlay drawn as a sibling of <Canvas>. MeasurementsProbe projects the
 * frame's points each frame and hands them over through `targetRef`; this
 * component mutates the SVG attributes directly rather than calling setState,
 * so the render loop never touches React.
 */
export default function MeasurementsGizmo({ targetRef }) {
  const {
    showMeasurements,
    valid,
    axes,
    totalWidth,
    totalHeight,
    widthLabelPosition,
    heightLabelPosition,
  } = useFrameMeasurements();
  const active = showMeasurements && valid;

  const [shouldRender, setShouldRender] = useState(active);
  const [opacity, setOpacity] = useState(0);

  // Keyed by axis id, never by array position: switching orientation changes
  // which axis sits at index 0, and React's cleanup call for the outgoing
  // element would otherwise null out the slot the incoming one just claimed.
  const axisRefs = useRef({});
  const widthLabelRef = useRef(null);
  const heightLabelRef = useRef(null);

  // "break line after each word" — the height label reads as a stacked column
  // beside the frame.
  const heightLines = ["Total", "Height:", formatCm(totalHeight)];

  const refsFor = (id) => {
    if (!axisRefs.current[id]) {
      axisRefs.current[id] = { line: null, ticks: null, labels: [] };
    }
    return axisRefs.current[id];
  };

  useEffect(() => {
    if (active) {
      setShouldRender(true);
      // Let the element mount at opacity 0 before transitioning in.
      const raf = requestAnimationFrame(() => setOpacity(1));
      return () => cancelAnimationFrame(raf);
    }
    setOpacity(0);
    const timer = setTimeout(() => setShouldRender(false), 200);
    return () => clearTimeout(timer);
  }, [active]);

  const update = useCallback(() => {
    const projected = targetRef.current?.axes;

    placeTotals(
      targetRef.current?.corners,
      widthLabelRef.current,
      heightLabelRef.current,
      widthLabelPosition,
      heightLabelPosition
    );

    axes.forEach((axis) => {
      // Match by id, not position: switching orientation reorders the list.
      const points = projected?.find((entry) => entry.id === axis.id)?.points;
      drawAxis(points, axis, refsFor(axis.id));
    });
  }, [targetRef, axes, widthLabelPosition, heightLabelPosition]);

  // Subscribe to the probe's per-frame tick.
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    target.onUpdate = update;
    return () => {
      if (target.onUpdate === update) target.onUpdate = null;
    };
  }, [targetRef, update]);

  // Reflect structural changes immediately rather than waiting a frame.
  useLayoutEffect(() => {
    if (shouldRender) update();
  }, [shouldRender, update]);

  if (!shouldRender) return null;

  return (
    <svg style={{ ...gizmoStyles.svg, opacity }} aria-hidden="true">
      <text
        ref={widthLabelRef}
        style={gizmoStyles.label}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        {`Total Width: ${formatCm(totalWidth)}`}
      </text>
      <text
        ref={heightLabelRef}
        style={gizmoStyles.label}
        dominantBaseline="middle"
        textAnchor={heightLabelPosition === "right" ? "start" : "end"}
      >
        {heightLines.map((word, index) => (
          <tspan
            key={word}
            x={0}
            dy={
              index === 0
                ? -((heightLines.length - 1) / 2) * TOTAL_LINE_HEIGHT
                : TOTAL_LINE_HEIGHT
            }
          >
            {word}
          </tspan>
        ))}
      </text>

      {axes.map((axis) => (
        <g key={axis.id}>
          <polyline
            ref={(el) => {
              // Ignore the null cleanup call — see axisRefs above.
              if (el) refsFor(axis.id).line = el;
            }}
            points=""
            fill="none"
            stroke={INK}
            strokeWidth={1.25}
            strokeLinecap="round"
          />
          <path
            ref={(el) => {
              if (el) refsFor(axis.id).ticks = el;
            }}
            d=""
            fill="none"
            stroke={INK}
            strokeWidth={1.25}
            strokeLinecap="round"
          />
          {axis.segments.map((segment, index) => (
            <text
              key={`${segment.startIndex}-${index}`}
              ref={(el) => {
                if (el) refsFor(axis.id).labels[index] = el;
              }}
              style={gizmoStyles.label}
              dominantBaseline="middle"
              // Centre the numbers along a horizontal run; on a vertical run
              // they read better hanging off the line.
              textAnchor={axis.id === "horizontal" ? "middle" : "start"}
            >
              {formatCm(segment.value)}
            </text>
          ))}
        </g>
      ))}
    </svg>
  );
}
